import { useEffect, useState } from 'react';

import { useSetAtom } from 'jotai';

import { SearchDocument } from '@cfa/pathway-fe-shared/xp';

import { UnkeyedDocument } from '@/hooks/useGetStructuredBrowseData';

import { useDocumentResource } from '@/utils/document-utils';

import {
  BrightcovePlayer,
  isBrightcovePlayerUrl,
} from '@/components/BrightcovePlayer';

import Constants from '@/constants';

import { BaseReferenceDTO } from '@/services/content-api-types';

import { Tridion } from '../document/views';

import {
  generateUrlFromReference,
  getDocumentIdFromCategoryResource,
  isBaseReferenceDTO,
  isCategoryDocument,
  isDocument,
  isLegacyBaseReferenceDTO,
  resolveDirectVideoUrl,
  tempProcessVideoSrc,
} from './utils';

import { isTridionPreviewAtom } from '@/atoms/is-tridion-preview';

interface DocumentPreviewProps {
  previewDocument: SearchDocument | BaseReferenceDTO | UnkeyedDocument;
  handleGoBack: () => void;
  isTemplatePreview?: boolean;
  isTridionPreview?: boolean;
}

const DocumentPreview: React.FC<DocumentPreviewProps> = ({
  previewDocument,
  handleGoBack,
  isTemplatePreview,
  isTridionPreview = true,
}) => {
  const [gameVideoUrl, setGameVideoURl] = useState<string | undefined>();

  const url = isLegacyBaseReferenceDTO(previewDocument)
    ? generateUrlFromReference(previewDocument)
    : // hover src and reference to check variable descriptions
      (tempProcessVideoSrc((previewDocument as BaseReferenceDTO).src) ??
      (previewDocument as BaseReferenceDTO).reference ??
      previewDocument.contentUrl ??
      previewDocument.contentApiUrl);

  const documentId = isCategoryDocument(previewDocument)
    ? getDocumentIdFromCategoryResource(previewDocument)
    : isBaseReferenceDTO(previewDocument)
      ? previewDocument.documentId
      : undefined;

  const { resource } = useDocumentResource(documentId, !url && !!documentId);

  const resourceReference = resource?.references?.[0];
  const referenceContentUrl =
    resourceReference?.contentUrl ?? resourceReference?.contentApiUrl;
  const referenceSourceSystem = resourceReference?.sourceSystem;
  const previewSourceSystem = previewDocument.sourceSystem?.toUpperCase();
  const normalizedReferenceSourceSystem = referenceSourceSystem?.toUpperCase();
  const previewType = previewDocument.type?.toUpperCase();
  const resourceType = resource?.type?.toUpperCase();

  const isTemplatePreviewGameOrVideo =
    isTemplatePreview &&
    (previewSourceSystem ===
      Constants.EXPANDED_FILE_FORMAT_TYPES.GAME.toUpperCase() ||
      previewSourceSystem ===
        Constants.EXPANDED_FILE_FORMAT_TYPES.BRIGHTCOVE.toUpperCase());

  const isBrightcoveByType =
    previewType === Constants.EXPANDED_FILE_FORMAT_TYPES.BRIGHTCOVE ||
    resourceType === Constants.EXPANDED_FILE_FORMAT_TYPES.BRIGHTCOVE;

  const isGameOrVideoReference =
    normalizedReferenceSourceSystem ===
      Constants.EXPANDED_FILE_FORMAT_TYPES.GAME.toUpperCase() ||
    normalizedReferenceSourceSystem ===
      Constants.EXPANDED_FILE_FORMAT_TYPES.BRIGHTCOVE.toUpperCase() ||
    isBrightcoveByType ||
    isTemplatePreviewGameOrVideo;

  const gameOrVideoReferenceContentUrl = isTemplatePreview
    ? previewDocument.contentUrl
    : referenceContentUrl;

  const candidateVideoUrl = gameOrVideoReferenceContentUrl ?? url;
  const normalizedCandidateVideoUrl = candidateVideoUrl?.toLowerCase();

  // Detect if this is a video/game based on URL or source system
  const hasVideoUrl =
    !!normalizedCandidateVideoUrl &&
    (normalizedCandidateVideoUrl.includes('/games-and-videos/') ||
      normalizedCandidateVideoUrl.includes('players.brightcove.net') ||
      normalizedCandidateVideoUrl.includes('/brightcove/'));

  const shouldTreatAsVideo = isGameOrVideoReference ? true : hasVideoUrl;
  const shouldInspectHtmlWrapper =
    shouldTreatAsVideo &&
    !!normalizedCandidateVideoUrl &&
    /\/index\.html(?:\?.*)?$/.test(normalizedCandidateVideoUrl);

  const tridionFromSearch =
    isDocument(previewDocument) && previewDocument.sourceSystem === 'TRIDION';
  const tridionFromBrowse = referenceSourceSystem === 'TRIDION';

  useEffect(() => {
    const shouldSkipVideoResolution =
      !candidateVideoUrl || (!shouldTreatAsVideo && !shouldInspectHtmlWrapper);

    if (shouldSkipVideoResolution) {
      return;
    }

    // Always fetch the HTML wrapper to extract the nested video URL
    (async () => {
      try {
        const result = await resolveDirectVideoUrl(candidateVideoUrl);
        setGameVideoURl(result);
      } catch (error) {
        console.warn('Video fetch failed:', error);
      }
    })();
  }, [
    shouldTreatAsVideo,
    shouldInspectHtmlWrapper,
    candidateVideoUrl,
    previewType,
    resourceType,
    previewSourceSystem,
    referenceSourceSystem,
    isBrightcoveByType,
    isGameOrVideoReference,
    hasVideoUrl,
  ]);

  const setIsTridionPreview = useSetAtom(isTridionPreviewAtom);

  useEffect(() => {
    setIsTridionPreview(tridionFromSearch || tridionFromBrowse);

    return () => {
      setIsTridionPreview(false);
    };
  }, [setIsTridionPreview, tridionFromSearch, tridionFromBrowse]);

  const resolvedVideoUrl = gameVideoUrl;
  const shouldRenderVideo = shouldTreatAsVideo || Boolean(resolvedVideoUrl);

  const contentUrl = shouldRenderVideo
    ? resolvedVideoUrl
    : (url ?? referenceContentUrl);

  if (tridionFromSearch && isDocument(previewDocument)) {
    return (
      <Tridion
        key={previewDocument.contentUrl ?? previewDocument.contentApiUrl ?? url}
        document={previewDocument}
        handleGoBack={handleGoBack}
        routedFrom="search"
        isPreview={isTridionPreview}
      />
    );
  }

  if (tridionFromBrowse && resourceReference) {
    return (
      <Tridion
        key={
          resourceReference.contentUrl ?? resourceReference.contentApiUrl ?? url
        }
        document={{ ...resourceReference, searchId: '' }}
        handleGoBack={handleGoBack}
        routedFrom="search"
        isPreview={isTridionPreview}
      />
    );
  }

  if (!contentUrl) {
    return null;
  }

  if (shouldRenderVideo) {
    return (
      <div className="flex size-full items-start justify-center overflow-y-auto p-4">
        <div className="w-full max-w-[960px]">
          {contentUrl && isBrightcovePlayerUrl(contentUrl) ? (
            <BrightcovePlayer
              className="content-iframe aspect-video w-full border-0 bg-black"
              src={contentUrl}
              title={resourceReference?.name ?? 'Preview'}
            />
          ) : (
            <iframe
              allow="fullscreen; autoplay; encrypted-media; picture-in-picture"
              className="content-iframe aspect-video w-full border-0 bg-black"
              src={contentUrl}
              style={{ height: '100%', width: '100%' }}
              title={resourceReference?.name ?? 'Preview'}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <iframe
      allow="fullscreen"
      className={
        isTemplatePreview
          ? 'content-iframe size-full min-h-[60vh]'
          : 'content-iframe size-full'
      }
      src={contentUrl}
      style={{ height: '100%', width: '100%', aspectRatio: 'auto' }}
      title={resourceReference?.name ?? 'Preview'}
    />
  );
};

export default DocumentPreview;
